import React, { useMemo, useState } from 'react';
import { Search, Box, ChevronRight, ChevronDown } from 'lucide-react';

const DND_NODE_TYPE = 'application/x-kquant-node'

export default function NodesPanel({ nodeDefs = [], onPickNode }) {
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const groups = useMemo(() => {
    const g = {};
    const safeDefs = Array.isArray(nodeDefs) ? nodeDefs : [];
    
    safeDefs.forEach(def => {
      const cat = def.category || 'Uncategorized';
      if (!g[cat]) g[cat] = [];
      g[cat].push(def);
    });

    const sortedKeys = Object.keys(g).sort((a, b) => a.localeCompare(b));
    
    const result = [];
    const lowerSearch = search.toLowerCase();

    sortedKeys.forEach(key => {
      const items = g[key].filter(def => 
        !search || 
        def.name.toLowerCase().includes(lowerSearch) || 
        (def.description && def.description.toLowerCase().includes(lowerSearch))
      ).sort((a, b) => a.name.localeCompare(b.name));

      if (items.length > 0) {
        result.push({ category: key, items });
      }
    });

    return result;
  }, [nodeDefs, search]);

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg-panel)]">
      <div className="p-3 border-b border-[var(--border-subtle)] bg-[var(--bg-app)]/50">
        <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
            <input 
                type="text" 
                placeholder="Search nodes..." 
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded-lg pl-9 pr-3 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none focus:border-[var(--accent-blue)] transition-colors"
            />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
        {groups.map(group => (
            <div key={group.category} className="mb-2">
                <button 
                    onClick={() => toggleCategory(group.category)}
                    className="flex items-center w-full text-left px-2 py-1.5 text-xs font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors uppercase tracking-wider"
                >
                    {collapsedCategories[group.category] ? <ChevronRight size={12} className="mr-1.5" /> : <ChevronDown size={12} className="mr-1.5" />}
                    {group.category}
                </button>
                
                {!collapsedCategories[group.category] && (
                    <div className="flex flex-col gap-1 mt-1 pl-2">
                        {group.items.map(node => (
                            <div
                                key={node.name}
                                role="button"
                                tabIndex={0}
                                draggable
                                onClick={() => onPickNode?.(node.name)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    onPickNode?.(node.name)
                                  }
                                }}
                                onDragStart={(e) => {
                                  e.dataTransfer.setData(DND_NODE_TYPE, node.name)
                                  e.dataTransfer.effectAllowed = 'move'
                                }}
                                className="group flex flex-col p-2 rounded-lg border border-transparent hover:border-[var(--border-subtle)] hover:bg-[var(--bg-panel-hover)] transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-[var(--accent-blue)]"
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded bg-[var(--accent-blue)]/10 text-[var(--accent-blue)] flex items-center justify-center shrink-0">
                                        <Box size={14} />
                                    </div>
                                    <span className="text-sm font-medium text-[var(--text-primary)]">{node.name}</span>
                                </div>
                                {node.description && (
                                    <p className="text-[10px] text-[var(--text-tertiary)] mt-1 line-clamp-2 pl-8 group-hover:text-[var(--text-secondary)]">
                                        {node.description}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ))}
        {groups.length === 0 && (
            <div className="p-4 text-center text-xs text-[var(--text-tertiary)]">
                No nodes found
            </div>
        )}
      </div>
    </div>
  );
}
